import api from './client';
import type {
  XmlCteResponse, XmlCteFilters, CteExportOptions, CteStatus,
  ProcessamentoResult, UploadCertificadoResponse, ConsultanteOpcao,
} from '../types/cte';

// CTe API para visualização de XMLs (similar ao XMLTerceiros)
class CteXmlApi {
  // Buscar XML de CTes com paginação e filtros
  async getXmlCtes(filters?: XmlCteFilters, page = 1, pageSize = 50) {
    const params = new URLSearchParams();
    
    // Parâmetros de paginação
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());
    
    // Filtro rápido de dias
    if (filters?.dias) {
      params.append('dias', filters.dias.toString());
    }
    
    // Filtro de busca geral
    if (filters?.search) {
      params.append('search', filters.search);
    }

    // Filtros de data personalizado
    if (filters?.dataInicio) {
      params.append('dataInicio', filters.dataInicio);
    }

    if (filters?.dataFim) {
      params.append('dataFim', filters.dataFim);
    }

    // Filtro por consultante (multi-CNPJ): quem baixou o CTe
    if (filters?.consultante) {
      params.append('consultante', filters.consultante);
    }

    const queryString = params.toString();
    const url = `/cte/xmls${queryString ? `?${queryString}` : ''}`;
    
    return api.get<XmlCteResponse>(url);
  }

  // Baixar XML
  async baixarXml(cteId: number) {
    const response = await api.get(`/cte/xmls/${cteId}/xml`, {
      responseType: 'blob',
    });
    
    // Criar URL para download do arquivo
    const blob = new Blob([response.data], { type: 'application/xml' });
    const url = window.URL.createObjectURL(blob);
    
    // Criar link de download
    const link = document.createElement('a');
    link.href = url;
    link.download = `CTe_${cteId}.xml`;
    document.body.appendChild(link);
    link.click();
    
    // Limpar recursos
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return response;
  }

  // Gerar e baixar DACTE PDF com Econet
  async gerarDacte(cteId: number) {
    const response = await api.get(`/cte/xmls/${cteId}/dacte-econet`, {
      responseType: 'blob',
    });
    
    // Criar URL para download do arquivo
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    
    // Criar link de download
    const link = document.createElement('a');
    link.href = url;
    link.download = `DACTE_${cteId}.pdf`;
    document.body.appendChild(link);
    link.click();
    
    // Limpar recursos
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return response;
  }

  // Exportar arquivo (genérico)
  async exportarArquivo(options: CteExportOptions) {
    return this.baixarXml(options.cteId);
  }

  // ── Manutenção (ADMIN — aba em Organização) ──────────────────────────────

  // Estado atual do processamento — 1 item por consultante (multi-CNPJ).
  async obterStatus() {
    return api.get<CteStatus>('/cte/status');
  }

  // Reprocessa um CTe específico pela chave de acesso (44 dígitos).
  // cnpj opcional: sem ele o backend tenta cada consultante ativo até sucesso.
  async reprocessarPorChave(chave: string, cnpj?: string) {
    const qs = cnpj ? `?cnpj=${encodeURIComponent(cnpj)}` : '';
    return api.post<ProcessamentoResult>(`/cte/processar-manual/${chave}${qs}`);
  }

  // Força um ciclo de consulta agora. cnpj opcional (sem ele: todos os ativos).
  async forcarCiclo(nsuInicial?: string, cnpj?: string) {
    const params = new URLSearchParams();
    if (nsuInicial) params.append('nsuInicial', nsuInicial);
    if (cnpj) params.append('cnpj', cnpj);
    const qs = params.toString();
    return api.post<ProcessamentoResult>(`/cte/processar${qs ? `?${qs}` : ''}`);
  }

  // Opções leves pro filtro "Consultante" do manifesto (não-admin).
  async consultantesOpcoes() {
    const r = await api.get<ConsultanteOpcao[]>('/cte/consultantes-opcoes');
    return r.data;
  }

  // ── Consultantes (CRUD + certificado) ─────────────────────────────────────

  async criarConsultante(cnpj: string, ufCodigo: string) {
    return api.post<{ sucesso: boolean; id: number }>('/cte/consultantes', { cnpj, ufCodigo });
  }

  async atualizarConsultante(id: number, ufCodigo: string, ativo: boolean) {
    return api.put<{ sucesso: boolean }>(`/cte/consultantes/${id}`, { ufCodigo, ativo });
  }

  async excluirConsultante(id: number) {
    return api.delete<{ sucesso: boolean }>(`/cte/consultantes/${id}`);
  }

  // Upload do PFX (multipart). A senha vai no form e NUNCA fica no estado da UI
  // além do submit; o backend valida abrindo o certificado na hora.
  async uploadCertificado(id: number, arquivo: File, senha: string) {
    const form = new FormData();
    form.append('arquivo', arquivo);
    form.append('senha', senha);
    const r = await api.post<UploadCertificadoResponse>(
      `/cte/consultantes/${id}/certificado`, form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return r.data;
  }
}

export default new CteXmlApi();